/* eslint-disable no-unused-vars */

import * as THREE from "three";
import Delaunator from "delaunator";

function computeNormal(points) {
  const v1 = new THREE.Vector3().subVectors(points[1], points[0]);
  const v2 = new THREE.Vector3().subVectors(points[2], points[0]);
  return new THREE.Vector3().crossVectors(v1, v2).normalize();
}

function projectPoints(points3D, normal) {
  // Corrigir cálculo do eixo dominante
  const axis = new THREE.Vector3(
    Math.abs(normal.x),
    Math.abs(normal.y),
    Math.abs(normal.z)
  );

  let dominante = "z";
  if (axis.x > axis.y && axis.x > axis.z) dominante = "x";
  else if (axis.y > axis.z) dominante = "y";

  return points3D.map((p) => {
    switch (dominante) {
      case "x":
        return [p.y, p.z]; // Projeta no plano YZ
      case "y":
        return [p.x, p.z]; // Projeta no plano XZ
      default:
        return [p.x, p.y]; // Projeta no plano XY
    }
  });
}

function createFanMesh(center, points3D, scene) {
  const geom = new THREE.BufferGeometry();
  const vertices = [];

  for (let i = 0; i < points3D.length - 1; i++) {
    const p1 = points3D[i];
    const p2 = points3D[i + 1];

    vertices.push(
      center.x,
      center.y,
      center.z,
      p1.x,
      p1.y,
      p1.z,
      p2.x,
      p2.y,
      p2.z
    );
  }

  geom.setAttribute("position", new THREE.Float32BufferAttribute(vertices, 3));
  geom.computeVertexNormals();

  const material = new THREE.MeshStandardMaterial({
    color: 0xff0000,
    side: THREE.DoubleSide,
    transparent: true,
    opacity: 0.6,
  });

  const mesh = new THREE.Mesh(geom, material);
  scene.add(mesh); // Supondo que você tenha a `scene` disponível
}

function densifyLoop(points3D, maxLen = 0.5) {
  const result = [];
  for (let i = 0; i < points3D.length - 1; i++) {
    const a = points3D[i], b = points3D[i+1];
    const dist = a.distanceTo(b);
    const steps = Math.ceil(dist / maxLen);
    for (let j = 0; j < steps; j++) {
      result.push(a.clone().lerp(b, j/steps));
    }
  }
  // fecha o loop
  result.push(result[0].clone());
  return result;
}

function laplacianSmooth(geometry, iterations = 10, lambda = 0.5) {
  const posAttr = geometry.attributes.position;
  const count  = posAttr.count; // número total de vértices
  // 1) Extrai todos os vértices num array de Vector3
  const vertices = [];
  for (let i = 0; i < count; i++) {
    vertices.push(new THREE.Vector3().fromBufferAttribute(posAttr, i));
  }

  // 2) Cria lista de vizinhos vazia para cada vértice
  const neighbors = Array.from({ length: count }, () => new Set());

  // 3) Preenche vizinhos, indexado ou não
  if (geometry.index) {
    // caso tenha índice
    const idx = geometry.index;
    for (let i = 0; i < idx.count; i += 3) {
      const a = idx.getX(i),
            b = idx.getX(i + 1),
            c = idx.getX(i + 2);
      neighbors[a].add(b).add(c);
      neighbors[b].add(a).add(c);
      neighbors[c].add(a).add(b);
    }
  } else {
    // não indexado: cada 3 vértices do position formam um triângulo
    const triCount = count / 3;
    for (let t = 0; t < triCount; t++) {
      const a = 3 * t, 
            b = 3 * t + 1, 
            c = 3 * t + 2;
      neighbors[a].add(b).add(c);
      neighbors[b].add(a).add(c);
      neighbors[c].add(a).add(b);
    }
  }

  // 4) Executa o Laplacian smoothing
  for (let it = 0; it < iterations; it++) {
    const deltas = vertices.map(() => new THREE.Vector3());
    vertices.forEach((v, i) => {
      const avg = new THREE.Vector3();
      neighbors[i].forEach(j => avg.add(vertices[j]));
      avg.divideScalar(neighbors[i].size);
      deltas[i].copy(avg.sub(v).multiplyScalar(lambda));
    });
    deltas.forEach((d, i) => vertices[i].add(d));
  }

  // 5) Grava de volta no atributo position
  for (let i = 0; i < count; i++) {
    const v = vertices[i];
    posAttr.setXYZ(i, v.x, v.y, v.z);
  }
  posAttr.needsUpdate = true;
}



export function fillLoopsIntoMesh(geometry, loops) {
  // 1) Limpamos qualquer índice ou normal pré-existente
  geometry.setIndex(null);
  geometry.deleteAttribute('normal');

  // (Opcional) ainda podemos recomputar a bounding box antiga
  geometry.computeBoundingBox();

  // 2) Pegamos o atributo position atual e convertemos para array
  const oldPos = geometry.getAttribute('position');
  const finalPositions = Array.from(oldPos.array);

  // 3) Montamos os "fans" de tampas
  loops.forEach(loopEdges => {
    const points3D = [];
    loopEdges.forEach((edge, i) => {
      const p = new THREE.Vector3(...edge.slice(0, 3));
      if (i === 0 || !p.equals(points3D[points3D.length - 1])) {
        points3D.push(p);
      }
    });
    if (points3D.length < 3) return;
    if (!points3D[0].equals(points3D[points3D.length - 1])) {
      points3D.push(points3D[0].clone());
    }

    const highResPoints = densifyLoop(points3D, /*tamanho máximo de aresta*/ 0.1);
    // calcula centro
    const center = highResPoints.reduce((c, p) => c.add(p), new THREE.Vector3()).divideScalar(highResPoints.length - 1);

    // gera triângulos center→p1→p2
    for (let i = 0; i < highResPoints.length - 1; i++) {
      const p1 = highResPoints[i], p2 = highResPoints[i + 1];
      finalPositions.push(center.x, center.y, center.z);
      finalPositions.push(p1.x, p1.y, p1.z);
      finalPositions.push(p2.x, p2.y, p2.z);
    }
  });

  // 4) Substitui o atributo position completo
  const merged = new Float32Array(finalPositions);
  geometry.setAttribute('position', new THREE.BufferAttribute(merged, 3));
  geometry.attributes.position.needsUpdate = true;

  // 5) Agora sim, computa normais para **cada** vértice novo
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
}


export function fillLoopsIntoMesh_(geometry, loops) {
  geometry.computeBoundingBox();

  // Garante que a geometria seja não indexada
  geometry.deleteAttribute("index");
  const oldPos = geometry.getAttribute("position");
  const finalPositions = Array.from(oldPos.array);

  loops.forEach((loopEdges) => {
    const points3D = [];

    // Converte as arestas em pontos 3D, evitando duplicatas consecutivas
    loopEdges.forEach((edge, index) => {
      const point = new THREE.Vector3(...edge.slice(0, 3));
      if (index === 0 || !point.equals(points3D[points3D.length - 1])) {
        points3D.push(point);
      }
    });

    if (points3D.length < 3) return;

    // Garante que o loop esteja fechado
    if (!points3D[0].equals(points3D[points3D.length - 1])) {
      points3D.push(points3D[0].clone());
    }

    // Calcula o centro do loop
    const center = new THREE.Vector3();
    points3D.forEach((p) => center.add(p));
    center.divideScalar(points3D.length - 1);

    // Calcula um plano aproximado para o loop.
    // Usamos os 3 primeiros pontos para definir um plano
    const v1 = new THREE.Vector3().subVectors(points3D[1], points3D[0]);
    const v2 = new THREE.Vector3().subVectors(points3D[2], points3D[0]);
    const normal = new THREE.Vector3().crossVectors(v1, v2).normalize();

    // Define um sistema de coordenadas local (xAxis, yAxis) no plano
    const xAxis = new THREE.Vector3();
    // Escolhe um vetor arbitrário para computar um eixo perpendicular
    if (Math.abs(normal.y) < 0.99) {
      xAxis.set(0, 1, 0).cross(normal).normalize();
    } else {
      xAxis.set(1, 0, 0).cross(normal).normalize();
    }
    const yAxis = new THREE.Vector3().crossVectors(normal, xAxis);

    // Projeta os pontos 3D para 2D (coordenadas locais) em relação ao centro
    const points2D = points3D.map((p) => {
      const relative = new THREE.Vector3().subVectors(p, center);
      return new THREE.Vector2(relative.dot(xAxis), relative.dot(yAxis));
    });

    // Cria o shape com os pontos 2D
    const shape = new THREE.Shape(points2D);

    // Gera a geometria a partir do shape. Internamente, THREE.ShapeGeometry usa Earcut para a triangulação.
    const shapeGeometry = new THREE.ShapeGeometry(shape);
    const shapePositions = shapeGeometry.attributes.position.array;
    const triangulatedPoints = [];

    // Os pontos gerados estão no sistema de coordenadas local (x e y, com z = 0).
    // “Levantamos” os vértices de volta para o espaço 3D.
    for (let i = 0; i < shapePositions.length; i += 3) {
      const localPoint = new THREE.Vector2(
        shapePositions[i],
        shapePositions[i + 1]
      );
      const worldPoint = new THREE.Vector3()
        .copy(center)
        .add(new THREE.Vector3().copy(xAxis).multiplyScalar(localPoint.x))
        .add(new THREE.Vector3().copy(yAxis).multiplyScalar(localPoint.y));
      triangulatedPoints.push(worldPoint);
    }

    // Usa os índices fornecidos por shapeGeometry para definir os triângulos
    const indices = shapeGeometry.index.array;
    for (let i = 0; i < indices.length; i += 3) {
      const a = triangulatedPoints[indices[i]];
      const b = triangulatedPoints[indices[i + 1]];
      const c = triangulatedPoints[indices[i + 2]];
      finalPositions.push(a.x, a.y, a.z);
      finalPositions.push(b.x, b.y, b.z);
      finalPositions.push(c.x, c.y, c.z);
    }
  });

  // Atualiza a geometria com a nova lista de posições
  const merged = new Float32Array(finalPositions);
  geometry.setAttribute("position", new THREE.BufferAttribute(merged, 3));
  geometry.attributes.position.needsUpdate = true;
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
}

export function createFilledLoops_(scene, loops) {
  loops.forEach((loopEdges) => {
    const points3D = [];

    // Coleta e verifica pontos únicos
    loopEdges.forEach((edge, index) => {
      const point = new THREE.Vector3(...edge.slice(0, 3));
      if (index === 0 || !point.equals(points3D[points3D.length - 1])) {
        points3D.push(point);
      }
    });

    // Validação crítica
    if (points3D.length < 3) {
      console.warn(`Loop inválido com apenas ${points3D.length} pontos`);
      return;
    }

    // Fechamento do loop
    if (!points3D[0].equals(points3D[points3D.length - 1])) {
      points3D.push(points3D[0].clone());
    }

    const normal = computeNormal(points3D);
    const points2D = projectPoints(points3D, normal);

    // Verificação da triangulação
    const delaunay = Delaunator.from(points2D);
    if (delaunay.triangles.length === 0) {
      console.warn("Falha na triangulação");
      return;
    }

    // Criação segura da geometria
    const geometry = new THREE.BufferGeometry();

    // Conversão explícita para Float32Array
    const vertices = new Float32Array(points3D.flatMap((p) => [p.x, p.y, p.z]));

    geometry.setAttribute("position", new THREE.BufferAttribute(vertices, 3));

    // Conversão para Uint16Array para índices
    geometry.setIndex(new THREE.Uint16BufferAttribute(delaunay.triangles, 1));

    // Cálculo de normais obrigatório
    geometry.computeVertexNormals();
    geometry.computeBoundingBox();

    // const material = new THREE.MeshStandardMaterial({
    //   color: new THREE.Color().setHSL(Math.random(), 0.5, 0.7),
    //   side: THREE.DoubleSide,
    //   flatShading: true
    // });

    const material = new THREE.MeshPhysicalMaterial({
      color: 0xf1c0c0, // Rosa mais suave e menos saturado
      roughness: 0.6, // Superfície mais opaca e realista
      metalness: 0.0, // Sem reflexo metálico
      transmission: 0.1, // Um leve toque de translucidez (menos do que antes)
      thickness: 0.2, // Camada fina para um efeito sutil de profundidade
      clearcoat: 0.1, // Menos camada reflexiva, evitando brilho excessivo
      clearcoatRoughness: 0.7, // Refração menos nítida para um visual mais natural
      transparent: true,
      side: THREE.DoubleSide,
      opacity: 0.95, // Maior opacidade para reduzir efeito de gel
    });

    const mesh = new THREE.Mesh(geometry, material);
    scene.add(mesh);
  });
}

export function computeVertexNeighbors(geometry) {
  const neighbors = {};
  const positions = geometry.attributes.position.array;

  if (geometry.index) {
    // 🔹 Processa geometria indexada
    const index = geometry.index.array;
    for (let i = 0; i < index.length; i += 3) {
      const a = index[i];
      const b = index[i + 1];
      const c = index[i + 2];

      if (!neighbors[a]) neighbors[a] = new Set();
      if (!neighbors[b]) neighbors[b] = new Set();
      if (!neighbors[c]) neighbors[c] = new Set();

      neighbors[a].add(b);
      neighbors[a].add(c);
      neighbors[b].add(a);
      neighbors[b].add(c);
      neighbors[c].add(a);
      neighbors[c].add(b);
    }
  } else {
    // 🔥 Nova lógica para geometrias não-indexadas
    const vertexMap = new Map(); // Mapeia posições para índices únicos

    for (let i = 0; i < positions.length; i += 9) {
      // Processa 3 vértices por triângulo (i += 9)
      for (let j = 0; j < 3; j++) {
        const idx = j * 3;
        const x = positions[i + idx];
        const y = positions[i + idx + 1];
        const z = positions[i + idx + 2];
        const key = `${x},${y},${z}`;

        // Atribui índice único para posições duplicadas
        if (!vertexMap.has(key)) {
          vertexMap.set(key, vertexMap.size);
        }
      }
    }

    // Cria lista de índices únicos para cada triângulo
    const uniqueIndices = [];
    for (let i = 0; i < positions.length; i += 9) {
      for (let j = 0; j < 3; j++) {
        const idx = j * 3;
        const x = positions[i + idx];
        const y = positions[i + idx + 1];
        const z = positions[i + idx + 2];
        const key = `${x},${y},${z}`;
        uniqueIndices.push(vertexMap.get(key));
      }
    }

    // Agora, processa como se fosse indexado
    for (let i = 0; i < uniqueIndices.length; i += 3) {
      const a = uniqueIndices[i];
      const b = uniqueIndices[i + 1];
      const c = uniqueIndices[i + 2];

      if (!neighbors[a]) neighbors[a] = new Set();
      if (!neighbors[b]) neighbors[b] = new Set();
      if (!neighbors[c]) neighbors[c] = new Set();

      neighbors[a].add(b).add(c);
      neighbors[b].add(a).add(c);
      neighbors[c].add(a).add(b);
    }
  }

  return Object.fromEntries(
    Object.entries(neighbors).map(([k, v]) => [k, Array.from(v)])
  );
}

function compareVertices(v1, v2) {
  for (let i = 0; i < 3; i++) {
    if (v1[i] < v2[i]) return -1;
    if (v1[i] > v2[i]) return 1;
  }
  return 0;
}

export function detectBoundaries(geometry) {
  const positions = geometry.attributes.position.array;
  const edges = new Map();

  for (let i = 0; i < positions.length; i += 9) {
    const v1 = [positions[i], positions[i + 1], positions[i + 2]];
    const v2 = [positions[i + 3], positions[i + 4], positions[i + 5]];
    const v3 = [positions[i + 6], positions[i + 7], positions[i + 8]];

    const createEdgeKey = (a, b) => {
      const comp = compareVertices(a, b);
      if (comp === 0) return a.concat(b).toString(); // Vértices iguais (não deve ocorrer)
      return comp < 0 ? a.concat(b).toString() : b.concat(a).toString();
    };

    const edge1 = createEdgeKey(v1, v2);
    const edge2 = createEdgeKey(v2, v3);
    const edge3 = createEdgeKey(v3, v1);

    [edge1, edge2, edge3].forEach((key) => {
      edges.set(key, (edges.get(key) || 0) + 1);
    });
  }

  const boundaryEdges = [];
  edges.forEach((count, key) => {
    if (count === 1) {
      boundaryEdges.push(key.split(",").map(Number));
    }
  });

  return boundaryEdges;
}

export function groupBoundaryEdges(boundaryEdges, epsilon = 0.0001) {
  const loops = [];
  const visited = new Set();

  function vertexKey(v, precision = 3) {
    return `${v[0].toFixed(precision)}_${v[1].toFixed(
      precision
    )}_${v[2].toFixed(precision)}`;
  }

  function compareVertices(v1, v2) {
    const dx = v1[0] - v2[0];
    const dy = v1[1] - v2[1];
    const dz = v1[2] - v2[2];
    return dx * dx + dy * dy + dz * dz < epsilon * epsilon;
  }

  const vertexMap = new Map();
  boundaryEdges.forEach((edge) => {
    const from = edge.slice(0, 3);
    const to = edge.slice(3, 6);
    [from, to].forEach((vertex) => {
      const key = vertexKey(vertex);
      if (!vertexMap.has(key)) vertexMap.set(key, []);
      vertexMap.get(key).push(edge);
    });
  });

  boundaryEdges.forEach((startEdge) => {
    const edgeKey = startEdge.join(",");
    if (visited.has(edgeKey)) return;

    const loop = [];
    let currentEdge = startEdge;
    let startVertex = startEdge.slice(0, 3);
    let currentVertex = startEdge.slice(3, 6);

    loop.push(currentEdge);
    visited.add(edgeKey);

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const candidates = vertexMap.get(vertexKey(currentVertex)) || [];
      let nextEdge = null;

      for (const candidate of candidates) {
        const candidateKey = candidate.join(",");
        if (visited.has(candidateKey)) continue;

        const candidateFrom = candidate.slice(0, 3);
        const candidateTo = candidate.slice(3, 6);

        if (compareVertices(candidateFrom, currentVertex)) {
          nextEdge = candidate;
        } else if (compareVertices(candidateTo, currentVertex)) {
          nextEdge = [...candidateTo, ...candidateFrom];
        }

        if (nextEdge) {
          loop.push(nextEdge);
          visited.add(candidateKey);
          currentVertex = nextEdge.slice(3, 6);
          break;
        }
      }

      if (!nextEdge) break;

      if (compareVertices(currentVertex, startVertex)) {
        if (loop.length >= 3) {
          loops.push(loop);
        }
        break;
      }
    }
  });

  return loops;
}

function detectBoundariesOld(geometry) {
  const edges = new Map();

  if (geometry.index) {
    // Processa geometria indexada
    const index = geometry.index.array;
    for (let i = 0; i < index.length; i += 3) {
      for (let j = 0; j < 3; j++) {
        const a = index[i + j];
        const b = index[i + ((j + 1) % 3)];
        const key = `${Math.min(a, b)}-${Math.max(a, b)}`;
        edges.set(key, (edges.get(key) || 0) + 1);
      }
    }
  } else {
    // Processa geometria não-indexada
    const positions = geometry.attributes.position.array;
    for (let i = 0; i < positions.length; i += 9) {
      // 3 vértices por triângulo
      const v1 = i / 3; // Primeiro vértice do triângulo
      const v2 = v1 + 1; // Segundo vértice
      const v3 = v1 + 2; // Terceiro vértice

      // Adiciona todas as arestas do triângulo
      [
        [v1, v2],
        [v2, v3],
        [v3, v1],
      ].forEach(([a, b]) => {
        const key = `${Math.min(a, b)}-${Math.max(a, b)}`;
        edges.set(key, (edges.get(key) || 0) + 1);
      });
    }
  }

  // Identifica arestas de borda (aparecem apenas uma vez)
  const boundaries = [];
  edges.forEach((count, key) => {
    if (count === 1) boundaries.push(key.split("-").map(Number));
  });

  return { boundaries };
}

export function createControlRings(geometry, ringSize = 2) {
  const neighbors = computeVertexNeighbors(geometry);
  const { boundaries } = detectBoundariesOld(geometry);

  if (!boundaries.length) {
    console.warn("Nenhuma borda detectada para criar anéis de controle");
    return [];
  }

  const controlRings = [];
  boundaries.forEach((boundary) => {
    let currentRing = [...boundary]; // Cria cópia para evitar referências
    for (let i = 0; i < ringSize; i++) {
      const nextRing = [];
      currentRing.forEach((v) => {
        neighbors[v]?.forEach((n) => {
          if (!nextRing.includes(n)) nextRing.push(n);
        });
      });
      if (!nextRing.length) break; // Para se não houver mais vértices
      controlRings.push(nextRing);
      currentRing = nextRing;
    }
  });
  return controlRings;
}

export function createSpatialHash(positions, cellSize = 2) {
  const hash = {};

  const getCellKey = (x, y, z) => {
    const cx = Math.floor(x / cellSize);
    const cy = Math.floor(y / cellSize);
    const cz = Math.floor(z / cellSize);
    return cx + cy * 1e4 + cz * 1e8; // Ajuste os multiplicadores conforme necessário
  };

  for (let i = 0; i < positions.length; i += 3) {
    const x = positions[i];
    const y = positions[i + 1];
    const z = positions[i + 2];

    // Gera a chave usando as coordenadas reais
    const key = getCellKey(x, y, z);

    if (!hash[key]) hash[key] = [];
    hash[key].push(i / 3); // Armazena o índice do vértice
  }

  return { hash, cellSize };
}
